import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNotifications } from './NotificationContext';
import { sampleApplications, samplePayments } from '../data/sampleData';
// Application status types
export type ApplicationStatus = 'pending' | 'approved' | 'rejected' | 'processing' | 'under_review' | 'awaiting_payment' | 'awaiting_documents' | 'on_hold';
// Application interface
export interface Application {
  id: string;
  referenceNumber: string;
  userId: string;
  userName: string;
  userEmail: string;
  serviceId: string;
  serviceName: string;
  mdaId: string;
  mdaName: string;
  status: ApplicationStatus;
  submittedAt: string;
  updatedAt: string;
  formData: Record<string, any>;
  fee: number;
  isPaid: boolean;
  comments?: string[];
  isSimulated?: boolean;
  timeline: {
    status: ApplicationStatus | 'submitted' | 'payment_received' | 'document_uploaded' | 'comment_added';
    timestamp: string;
    note?: string;
    userId?: string;
    userName?: string;
  }[];
  documents?: {
    id: string;
    name: string;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
    type: string;
    size: number;
    status: 'pending' | 'approved' | 'rejected';
  }[];
  assignedTo?: {
    userId: string;
    userName: string;
    assignedAt: string;
  };
}
// Payment interface
export interface Payment {
  id: string;
  referenceNumber: string;
  applicationId: string;
  userId: string;
  userName: string;
  amount: number;
  serviceName: string;
  mdaId: string;
  mdaName: string;
  status: 'pending' | 'successful' | 'failed';
  date: string;
  paymentMethod?: string;
  transactionId?: string;
  isSimulated?: boolean;
}
interface ApplicationContextType {
  applications: Application[];
  payments: Payment[];
  submitApplication: (applicationData: Omit<Application, 'id' | 'referenceNumber' | 'submittedAt' | 'updatedAt' | 'status' | 'timeline'>) => Application;
  updateApplicationStatus: (id: string, status: ApplicationStatus, note?: string, userId?: string, userName?: string) => void;
  getApplicationById: (id: string) => Application | undefined;
  getApplicationsByUserId: (userId: string) => Application[];
  getApplicationsByMdaId: (mdaId: string) => Application[];
  getApplicationByReferenceNumber: (referenceNumber: string) => Application | undefined;
  makePayment: (paymentData: Omit<Payment, 'id' | 'referenceNumber' | 'date' | 'status'>) => Payment;
  getPaymentsByUserId: (userId: string) => Payment[];
  getPaymentsByMdaId: (mdaId: string) => Payment[];
  getPaymentsByApplicationId: (applicationId: string) => Payment[];
  getRecentApplications: (limit?: number) => Application[];
  getRecentPayments: (limit?: number) => Payment[];
  getApplicationStats: () => {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    processing: number;
  };
  getPaymentStats: () => {
    total: number;
    successful: number;
    pending: number;
    failed: number;
    totalAmount: number;
  };
  searchApplications: (query: string) => Application[];
  addCommentToApplication: (applicationId: string, comment: string, userId: string, userName: string) => void;
  uploadDocumentToApplication: (applicationId: string, document: Omit<Application['documents'][0], 'id' | 'uploadedAt' | 'status'>) => void;
  assignApplication: (applicationId: string, userId: string, userName: string) => void;
  simulateApplication: (applicationData: Omit<Application, 'id' | 'referenceNumber' | 'submittedAt' | 'updatedAt' | 'status' | 'timeline' | 'isSimulated'>) => Application;
}
const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);
// Generate a service-specific prefix from the service name
const getServicePrefix = (serviceName: string): string => {
  if (!serviceName) return 'APP';
  // Check for common service types and return appropriate prefixes
  const serviceNameLower = serviceName.toLowerCase();
  if (serviceNameLower.includes('tax')) return 'TAX';
  if (serviceNameLower.includes('business') || serviceNameLower.includes('registration')) return 'BUS';
  if (serviceNameLower.includes('permit')) return 'PER';
  if (serviceNameLower.includes('license')) return 'LIC';
  if (serviceNameLower.includes('certificate')) return 'CER';
  if (serviceNameLower.includes('land') || serviceNameLower.includes('property')) return 'LND';
  if (serviceNameLower.includes('health') || serviceNameLower.includes('medical')) return 'HLT';
  if (serviceNameLower.includes('education') || serviceNameLower.includes('school')) return 'EDU';
  if (serviceNameLower.includes('agriculture') || serviceNameLower.includes('farm')) return 'AGR';
  if (serviceNameLower.includes('tourism')) return 'TRM';
  // If no specific match, take the first 3 letters of the service name
  const words = serviceName.split(' ');
  if (words.length > 0 && words[0].length >= 3) {
    return words[0].substring(0, 3).toUpperCase();
  }
  // Fallback to default prefix
  return 'APP';
};
// Generate a formatted reference number
const generateReferenceNumber = (serviceName: string): string => {
  const date = new Date();
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  // Get prefix based on service name
  const prefix = getServicePrefix(serviceName);
  // Format: PREFIX-YEAR-RANDOM
  return `${prefix}-${year}-${random}`;
};
export const ApplicationProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const notificationsContext = useNotifications();
  // Load initial data from localStorage if available or use sample data
  useEffect(() => {
    const savedApplications = localStorage.getItem('applications');
    const savedPayments = localStorage.getItem('payments');
    if (savedApplications) {
      setApplications(JSON.parse(savedApplications));
    } else {
      // Load sample data if no saved data exists
      setApplications(sampleApplications);
      localStorage.setItem('applications', JSON.stringify(sampleApplications));
    }
    if (savedPayments) {
      setPayments(JSON.parse(savedPayments));
    } else {
      // Load sample data if no saved data exists
      setPayments(samplePayments);
      localStorage.setItem('payments', JSON.stringify(samplePayments));
    }
  }, []);
  // Save applications and payments to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('applications', JSON.stringify(applications));
  }, [applications]);
  useEffect(() => {
    localStorage.setItem('payments', JSON.stringify(payments));
  }, [payments]);
  // Get applications by user ID
  const getApplicationsByUserId = useCallback((userId: string) => {
    return applications.filter(app => app.userId === userId);
  }, [applications]);
  // Get payments by user ID
  const getPaymentsByUserId = useCallback((userId: string) => {
    return payments.filter(payment => payment.userId === userId);
  }, [payments]);
  // Get applications by MDA ID
  const getApplicationsByMdaId = useCallback((mdaId: string) => {
    return applications.filter(app => app.mdaId === mdaId);
  }, [applications]);
  // Get application by ID
  const getApplicationById = useCallback((id: string) => {
    return applications.find(app => app.id === id);
  }, [applications]);
  // Get application by reference number
  const getApplicationByReferenceNumber = useCallback((referenceNumber: string) => {
    return applications.find(app => app.referenceNumber === referenceNumber);
  }, [applications]);
  // Get payments by MDA ID
  const getPaymentsByMdaId = useCallback((mdaId: string) => {
    return payments.filter(payment => payment.mdaId === mdaId);
  }, [payments]);
  // Get payments by application ID
  const getPaymentsByApplicationId = useCallback((applicationId: string) => {
    return payments.filter(payment => payment.applicationId === applicationId);
  }, [payments]);
  // Get recent applications
  const getRecentApplications = useCallback((limit: number = 5) => {
    return [...applications].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()).slice(0, limit);
  }, [applications]);
  // Get recent payments
  const getRecentPayments = useCallback((limit: number = 5) => {
    return [...payments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, limit);
  }, [payments]);
  // Get application statistics
  const getApplicationStats = useCallback(() => {
    const total = applications.length;
    const pending = applications.filter(app => app.status === 'pending').length;
    const approved = applications.filter(app => app.status === 'approved').length;
    const rejected = applications.filter(app => app.status === 'rejected').length;
    const processing = applications.filter(app => app.status === 'processing' || app.status === 'under_review' || app.status === 'awaiting_documents').length;
    return {
      total,
      pending,
      approved,
      rejected,
      processing
    };
  }, [applications]);
  // Get payment statistics
  const getPaymentStats = useCallback(() => {
    const total = payments.length;
    const successful = payments.filter(payment => payment.status === 'successful').length;
    const pending = payments.filter(payment => payment.status === 'pending').length;
    const failed = payments.filter(payment => payment.status === 'failed').length;
    const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
    return {
      total,
      successful,
      pending,
      failed,
      totalAmount
    };
  }, [payments]);
  // Submit a new application
  const submitApplication = useCallback((applicationData: Omit<Application, 'id' | 'referenceNumber' | 'submittedAt' | 'updatedAt' | 'status' | 'timeline'>) => {
    const now = new Date().toISOString();
    const referenceNumber = generateReferenceNumber(applicationData.serviceName);
    const newApplication: Application = {
      ...applicationData,
      id: uuidv4(),
      referenceNumber,
      submittedAt: now,
      updatedAt: now,
      status: 'pending',
      timeline: [{
        status: 'submitted',
        timestamp: now,
        note: 'Application submitted successfully'
      }]
    };
    setApplications(prev => [...prev, newApplication]);
    // Create a notification for the new application
    notificationsContext.createNotification({
      userId: applicationData.userId,
      type: 'application_submitted',
      title: 'Application Submitted',
      message: `Your application for ${applicationData.serviceName} has been submitted successfully. Reference Number: ${referenceNumber}`,
      referenceNumber,
      applicationId: newApplication.id,
      serviceId: applicationData.serviceId,
      mdaId: applicationData.mdaId,
      channels: ['email', 'in_app']
    });
    return newApplication;
  }, [notificationsContext]);
  // Update application status
  const updateApplicationStatus = useCallback((id: string, status: ApplicationStatus, note?: string, userId?: string, userName?: string) => {
    const now = new Date().toISOString();
    setApplications(prev => prev.map(app => {
      if (app.id === id) {
        const updatedApp = {
          ...app,
          status,
          updatedAt: now,
          timeline: [...app.timeline, {
            status,
            timestamp: now,
            note: note || `Status updated to ${status}`,
            userId,
            userName
          }]
        };
        // Create a notification for status update
        const notificationType = status === 'approved' ? 'application_approved' : status === 'rejected' ? 'application_rejected' : 'status_updated';
        const notificationTitle = `Application ${status.charAt(0).toUpperCase() + status.slice(1)}`;
        notificationsContext.createNotification({
          userId: app.userId,
          type: notificationType,
          title: notificationTitle,
          message: `Your application (${app.referenceNumber}) for ${app.serviceName} has been ${status}.`,
          referenceNumber: app.referenceNumber,
          applicationId: app.id,
          serviceId: app.serviceId,
          mdaId: app.mdaId,
          channels: ['email', 'sms', 'in_app']
        });
        return updatedApp;
      }
      return app;
    }));
  }, [notificationsContext]);
  // Make a payment
  const makePayment = useCallback((paymentData: Omit<Payment, 'id' | 'referenceNumber' | 'date' | 'status'>) => {
    const now = new Date().toISOString();
    const referenceNumber = `PMT-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const newPayment: Payment = {
      ...paymentData,
      id: uuidv4(),
      referenceNumber,
      date: now,
      status: 'successful' // Default to successful for demo
    };
    setPayments(prev => [...prev, newPayment]);
    // Update the application to mark it as paid
    setApplications(prev => prev.map(app => {
      if (app.id === paymentData.applicationId) {
        const updatedApp = {
          ...app,
          isPaid: true,
          updatedAt: now,
          timeline: [...app.timeline, {
            status: 'payment_received',
            timestamp: now,
            note: `Payment of ₦${paymentData.amount.toLocaleString()} received`
          }]
        };
        // Create a notification for payment
        notificationsContext.createNotification({
          userId: app.userId,
          type: 'payment_completed',
          title: 'Payment Successful',
          message: `Your payment of ₦${paymentData.amount.toLocaleString()} for ${app.serviceName} has been received. Your application is now being processed.`,
          referenceNumber: app.referenceNumber,
          applicationId: app.id,
          serviceId: app.serviceId,
          mdaId: app.mdaId,
          channels: ['email', 'sms', 'in_app']
        });
        return updatedApp;
      }
      return app;
    }));
    return newPayment;
  }, [notificationsContext]);
  // Search applications
  const searchApplications = useCallback((query: string) => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase().trim();
    return applications.filter(app => {
      return app.referenceNumber.toLowerCase().includes(lowerQuery) || app.serviceName.toLowerCase().includes(lowerQuery) || app.mdaName.toLowerCase().includes(lowerQuery) || app.userName.toLowerCase().includes(lowerQuery) || app.status.toLowerCase().includes(lowerQuery);
    });
  }, [applications]);
  // Add comment to application
  const addCommentToApplication = useCallback((applicationId: string, comment: string, userId: string, userName: string) => {
    const now = new Date().toISOString();
    setApplications(prev => prev.map(app => {
      if (app.id === applicationId) {
        const comments = app.comments || [];
        const updatedApp = {
          ...app,
          comments: [...comments, comment],
          updatedAt: now,
          timeline: [...app.timeline, {
            status: 'comment_added',
            timestamp: now,
            note: comment,
            userId,
            userName
          }]
        };
        // Create a notification for comment
        notificationsContext.createNotification({
          userId: app.userId,
          type: 'application_comment',
          title: 'New Comment on Your Application',
          message: `A new comment has been added to your ${app.serviceName} application.`,
          referenceNumber: app.referenceNumber,
          applicationId: app.id,
          serviceId: app.serviceId,
          mdaId: app.mdaId,
          channels: ['in_app']
        });
        return updatedApp;
      }
      return app;
    }));
  }, [notificationsContext]);
  // Upload document to application
  const uploadDocumentToApplication = useCallback((applicationId: string, document: Omit<Application['documents'][0], 'id' | 'uploadedAt' | 'status'>) => {
    const now = new Date().toISOString();
    setApplications(prev => prev.map(app => {
      if (app.id === applicationId) {
        const documents = app.documents || [];
        const newDocument = {
          ...document,
          id: uuidv4(),
          uploadedAt: now,
          status: 'pending'
        };
        const updatedApp = {
          ...app,
          documents: [...documents, newDocument],
          updatedAt: now,
          timeline: [...app.timeline, {
            status: 'document_uploaded',
            timestamp: now,
            note: `Document "${document.name}" uploaded`,
            userId: document.uploadedBy,
            userName: document.uploadedBy
          }]
        };
        // Create a notification for document upload
        notificationsContext.createNotification({
          userId: app.userId,
          type: 'document_uploaded',
          title: 'Document Uploaded',
          message: `A new document has been uploaded to your ${app.serviceName} application.`,
          referenceNumber: app.referenceNumber,
          applicationId: app.id,
          serviceId: app.serviceId,
          mdaId: app.mdaId,
          channels: ['in_app']
        });
        return updatedApp;
      }
      return app;
    }));
  }, [notificationsContext]);
  // Assign application to staff
  const assignApplication = useCallback((applicationId: string, userId: string, userName: string) => {
    const now = new Date().toISOString();
    setApplications(prev => prev.map(app => {
      if (app.id === applicationId) {
        return {
          ...app,
          assignedTo: {
            userId,
            userName,
            assignedAt: now
          },
          updatedAt: now,
          timeline: [...app.timeline, {
            status: 'processing',
            timestamp: now,
            note: `Application assigned to ${userName}`,
            userId,
            userName
          }]
        };
      }
      return app;
    }));
  }, []);
  // Simulate application for testing
  const simulateApplication = useCallback((applicationData: Omit<Application, 'id' | 'referenceNumber' | 'submittedAt' | 'updatedAt' | 'status' | 'timeline' | 'isSimulated'>) => {
    const application = submitApplication({
      ...applicationData
    });
    // Mark as simulated
    setApplications(prev => prev.map(app => {
      if (app.id === application.id) {
        return {
          ...app,
          isSimulated: true
        };
      }
      return app;
    }));
    return {
      ...application,
      isSimulated: true
    };
  }, [submitApplication]);
  return <ApplicationContext.Provider value={{
    applications,
    payments,
    submitApplication,
    updateApplicationStatus,
    getApplicationById,
    getApplicationsByUserId,
    getApplicationsByMdaId,
    getApplicationByReferenceNumber,
    makePayment,
    getPaymentsByUserId,
    getPaymentsByMdaId,
    getPaymentsByApplicationId,
    getRecentApplications,
    getRecentPayments,
    getApplicationStats,
    getPaymentStats,
    searchApplications,
    addCommentToApplication,
    uploadDocumentToApplication,
    assignApplication,
    simulateApplication
  }} data-id="element-816">
      {children}
    </ApplicationContext.Provider>;
};
export const useApplications = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplications must be used within an ApplicationProvider');
  }
  return context;
};